/**
 * Score how well an audited surgery matches the surgery the PATIENT recorded.
 *
 * Used only to ORDER search results. It never selects, filters or fills anything — the admin still
 * picks from the full list. The signal is deliberately the patient's own recorded surgery text
 * rather than their home location: ranking by proximity to where someone lives assumes they
 * registered near home, which is exactly the guess this feature must not make.
 *
 * Mirrors the backend normaliser (src/services/surgeryContacts/surgeryNormalise.js) so the ordering
 * the admin sees agrees with how the server matched.
 */

// Words shared by most practice names, so they say nothing about WHICH practice this is.
const GENERIC = [
    'surgery', 'practice', 'medical', 'centre', 'center', 'health', 'the', 'group', 'clinic',
    'partnership', 'and', 'of', 'care', 'primary', 'family', 'dr', 'drs', 'general', 'healthcare',
    'doctors', 'pms', 'gms',
];

export const normaliseSurgeryText = (value) => {
    if (!value) {
        return '';
    }
    let text = String(value).replace(/\([^)]*\)/g, ' ');
    text = text.toLowerCase().replace(/&/g, ' and ');
    text = text.replace(/[.,'’/]/g, ' ').replace(/-/g, ' ');
    text = text.replace(/\s+/g, ' ').trim();
    return text.indexOf('the ') === 0 ? text.slice(4) : text;
};

export const distinctiveTokens = (value) => normaliseSurgeryText(value)
    .split(' ')
    .filter((token) => token && GENERIC.indexOf(token) === -1);

/**
 * Fraction of the candidate's distinctive words that appear in the patient's recorded text, with a
 * bump when the postcode is mentioned there too. 0 when there is nothing to compare.
 */
export const scoreAgainstPatientSurgery = (candidate, patientSurgeryText) => {
    if (!patientSurgeryText || !candidate) {
        return 0;
    }

    const patientTokens = distinctiveTokens(patientSurgeryText);
    if (!patientTokens.length) {
        return 0;
    }

    const candidateTokens = distinctiveTokens(candidate.practiceName);
    if (!candidateTokens.length) {
        return 0;
    }

    const shared = candidateTokens.filter((token) => patientTokens.indexOf(token) !== -1).length;
    let score = shared / candidateTokens.length;

    const postcode = (candidate.postcode || '').replace(/\s+/g, '').toLowerCase();
    const patientCompact = normaliseSurgeryText(patientSurgeryText).replace(/\s+/g, '');
    if (postcode && patientCompact.indexOf(postcode) !== -1) {
        score += 1;
    }

    return score;
};

/**
 * Stable ordering: best match first, alphabetical among equals (including when there is no recorded
 * text at all, which reproduces today's behaviour exactly).
 */
export const rankSurgeriesByPatientText = (results, patientSurgeryText) => (results || [])
    .map((item, index) => ({ item, index, score: scoreAgainstPatientSurgery(item, patientSurgeryText) }))
    .sort((a, b) => {
        if (b.score !== a.score) {
            return b.score - a.score;
        }
        const nameA = (a.item.practiceName || '').toLowerCase();
        const nameB = (b.item.practiceName || '').toLowerCase();
        if (nameA !== nameB) {
            return nameA < nameB ? -1 : 1;
        }
        return a.index - b.index;
    })
    .map((entry) => entry.item);
